import React from 'react';

import { TABLE_PALETTE } from '@/components/ui/tables/Table.constants';
import { Text } from '@/components/ui/text/Text';
import { useTranslation } from '@/i18n';
import { stripSecondsFromTimes } from '@/utils';
import type { OvertimeApprovalDetailRow } from '@/api/domains/overtime/overtimeApproval.types';


export interface OvertimeApprovalDetailExpandedRowProps {
  row: OvertimeApprovalDetailRow;
  /** Resuelto server-side (caConfiguracionAsistencia.HHEEFlujoAprobacion) — gobierna si el
   *  comentario del revisor se muestra por separado del comentario del aprobador. */
  overtimeApprovalFlowEnabled: boolean;
}

/** Igual convención de horario usada en otras pantallas de asistencia (p. ej. MyMonthlyAttendance):
 *  entrada - almuerzo | almuerzo - salida cuando hay almuerzo registrado, o entrada - salida si
 *  no. Las marcas llegan del SP legado como HH:MM:SS; los segundos no aportan a la lectura, así que
 *  se recortan — vía `stripSecondsFromTimes` y no `toHHmm`, porque una fila de semana compensada
 *  trae texto ('Semana Compensada') donde debería ir la hora y truncarlo lo dejaría ilegible. */
function buildMarksText(row: OvertimeApprovalDetailRow): string | null {
  if (!row.entryTime || !row.exitTime) return null;

  const marks = row.lunchStart && row.lunchEnd
    ? `${row.entryTime} - ${row.lunchStart} | ${row.lunchEnd} - ${row.exitTime}`
    : `${row.entryTime} - ${row.exitTime}`;

  return stripSecondsFromTimes(marks);
}

interface DetailLineProps {
  label: string;
  value: string;
  'data-testid'?: string;
}

/** Una sección de la fila expandida: label en negrita, valor en peso normal, en la misma línea. */
const DetailLine: React.FC<DetailLineProps> = ({ label, value, 'data-testid': dataTestId }) => (
  <div className="flex flex-row flex-wrap gap-1" data-testid={dataTestId}>
    <Text variant="small-medium" color={TABLE_PALETTE.rowText} className="font-bold">
      {`${label}:`}
    </Text>
    <Text variant="small-regular" color={TABLE_PALETTE.rowText} className="whitespace-pre-line">
      {value}
    </Text>
  </div>
);

/**
 * Fila expandida del detalle de horas extras (ASIS-5355). Dos columnas: Horario y Marcaje a la
 * izquierda, el/los comentarios a la derecha. Cada sección lleva su label en negrita y su valor en
 * peso normal. Sin fondo propio: la tabla se declara con `highlightBackground: false` para que la
 * fila expandida use el mismo fondo que el resto.
 *
 * Con `overtimeApprovalFlowEnabled` en `false` solo existe `approverComment` y se rotula
 * "Comentario"; en `true`, cuando el backend además entrega `reviewerComment` (con su
 * `reviewerJustificationType`), cada comentario se muestra en su propia línea rotulada por rol —
 * nunca concatenados.
 */
export const OvertimeApprovalDetailExpandedRow: React.FC<
  OvertimeApprovalDetailExpandedRowProps
> = ({
  row,
  overtimeApprovalFlowEnabled,
}) => {
  const { t: translate } = useTranslation();

  const marksText = buildMarksText(row);
  const showReviewerComment = overtimeApprovalFlowEnabled && Boolean(row.reviewerComment);
  const showApproverComment = Boolean(row.approverComment);
  const showNoComments = !showReviewerComment && !showApproverComment;

  const reviewerCommentLabel = row.reviewerJustificationType
    ? `${translate('attendance:overtimeApproval.detail.expanded.reviewerComment')} (${row.reviewerJustificationType})`
    : translate('attendance:overtimeApproval.detail.expanded.reviewerComment');

  // Con un solo comentario visible no hay ambigüedad de rol: se rotula simplemente "Comentario".
  const approverCommentLabel = showReviewerComment
    ? translate('attendance:overtimeApproval.detail.expanded.approverComment')
    : translate('attendance:overtimeApproval.detail.expanded.comment');

  return (
    <div
      className="py-3 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1"
      data-testid="overtime-approval-detail-expanded-row"
    >
      <div className="flex flex-col gap-1">
        <DetailLine
          label={translate('attendance:overtimeApproval.detail.expanded.scheduleTitle')}
          value={
            // El SP arma el horario como "Nombre HH:MM:SS/HH:MM:SS": el recorte de segundos aplica
            // sobre las horas embebidas sin tocar el nombre del turno.
            row.scheduleLabel
              ? stripSecondsFromTimes(row.scheduleLabel)
              : translate('attendance:overtimeApproval.detail.expanded.noSchedule')
          }
          data-testid="overtime-approval-detail-schedule"
        />

        <DetailLine
          label={translate('attendance:overtimeApproval.detail.expanded.marks')}
          value={marksText ?? translate('attendance:overtimeApproval.detail.expanded.noMarks')}
          data-testid="overtime-approval-detail-marks"
        />
      </div>

      <div className="flex flex-col gap-1">
        {showReviewerComment && (
          <DetailLine
            label={reviewerCommentLabel}
            value={row.reviewerComment ?? ''}
            data-testid="overtime-approval-detail-reviewer-comment"
          />
        )}

        {showApproverComment && (
          <DetailLine
            label={approverCommentLabel}
            value={row.approverComment ?? ''}
            data-testid="overtime-approval-detail-approver-comment"
          />
        )}

        {showNoComments && (
          <DetailLine
            label={translate('attendance:overtimeApproval.detail.expanded.comment')}
            value={translate('attendance:overtimeApproval.detail.expanded.noComments')}
            data-testid="overtime-approval-detail-no-comments"
          />
        )}
      </div>
    </div>
  );
};

export default OvertimeApprovalDetailExpandedRow;
